/** Tarih / etiket biçimlendirme. Sayılar için `lib/decimal.ts` kullanılır. */

import type { AssetBinding, IsoDate, IsoTimestamp, TransactionType } from "@parafon/contracts";
import type { AccountKind } from "@parafon/contracts/local-api";
import { CURRENCIES } from "@parafon/contracts";
import { DIL, M, YEREL } from "../dil";

const dateFmt = new Intl.DateTimeFormat(YEREL, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

const stampFmt = new Intl.DateTimeFormat(YEREL, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "UTC",
});

/** "2026-09-03" → "03.09.2026". Geçersiz girdi olduğu gibi geri döner. */
export function formatIsoDate(value: IsoDate | undefined | null): string {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return value;
  return dateFmt.format(d);
}

export function formatIsoTimestamp(value: IsoTimestamp | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return stampFmt.format(d);
}

/** Bugünün ISO tarihi (UTC gün sınırı — sözleşme gün hassasiyetinde). */
export function todayIso(): IsoDate {
  return new Date().toISOString().slice(0, 10);
}

export const ACCOUNT_KIND_LABEL: Readonly<Record<AccountKind, string>> = M.bicim.hesapTuru;

export const ACCOUNT_KINDS: readonly AccountKind[] = [
  "araci_kurum",
  "banka",
  "cuzdan",
  "borsa",
  "fiziki",
];

export const TRANSACTION_TYPE_LABEL: Readonly<Record<TransactionType, string>> = M.bicim.hareketTuru;

/**
 * Bir hareketin ekrandaki adı. Döviz alış/satış YÖNÜYLE adlanır (kullanıcı
 * kararı 22.10.2026): TL verilip döviz alındıysa "Döviz alış", döviz verilip
 * TL alındıysa "Döviz satış"; iki döviz arasında "Döviz alış/satış".
 */
export function hareketAdi(t: { readonly type: TransactionType; readonly currency: string; readonly alinanParaBirimi?: string }): string {
  if (t.type !== "FX_CONVERSION") return TRANSACTION_TYPE_LABEL[t.type];
  if (t.currency === "TRY") return M.bicim.dovizAlis;
  if (t.alinanParaBirimi === "TRY") return M.bicim.dovizSatis;
  return TRANSACTION_TYPE_LABEL.FX_CONVERSION;
}

export const TRANSACTION_TYPES: readonly TransactionType[] = [
  "BUY",
  "SELL",
  "DIVIDEND",
  "INTEREST",
  "SPLIT",
  "BONUS",
  "RIGHTS",
  "DEPOSIT",
  "WITHDRAWAL",
  "FEE",
  "TAX_PAYMENT",
  "FX_CONVERSION",
  "TRANSFER_IN",
  "TRANSFER_OUT",
];

/** Varlık bağlamasının insan okuyacağı adı. */
export function assetLabel(asset: AssetBinding): string {
  return asset.kind === "tracked" ? asset.instrumentId : asset.label;
}

/** Aynı varlığı gruplamak için kararlı anahtar. */
export function assetKey(asset: AssetBinding): string {
  return asset.kind === "tracked"
    ? `tracked:${asset.instrumentId}`
    : `manual:${asset.label} ${asset.currency}`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return M.bicim.bilinmiyor;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : 1;
  return `${new Intl.NumberFormat(YEREL, { maximumFractionDigits: digits }).format(v)} ${units[i]}`;
}

export function formatCount(n: number): string {
  return new Intl.NumberFormat(YEREL).format(n);
}

/** Varlık sınıfının etkin dildeki adı. Bilinmeyen kod OLDUĞU GİBİ gösterilir. */
export const ASSET_CLASS_LABEL: Readonly<Record<string, string>> = M.bicim.varlikSinifi;

export function assetClassLabel(code: string | null | undefined): string {
  if (code === null || code === undefined || code === "") return M.bicim.sinifiBilinmiyor;
  return ASSET_CLASS_LABEL[code] ?? code;
}

/**
 * Para birimi gösterimi. Yahoo `search` para birimi vermiyor ve bağlayıcı
 * bilinçli olarak `"?"` bırakıyor — TAHMİN ETMEK sessiz bir değerleme hatası
 * olurdu. Arayüz bunu "bilinmiyor" diye gösterir, boş bırakıp geçmez.
 */
export function currencyLabel(code: string | null | undefined): string {
  if (code === null || code === undefined || code.trim() === "" || code === "?") {
    return M.bicim.bilinmiyor;
  }
  return code;
}

/** Para birimi merkezden gelmemiş mi (kullanıcının doldurması gerekir). */
export function isCurrencyUnknown(code: string | null | undefined): boolean {
  return code === null || code === undefined || code.trim() === "" || code === "?";
}

/**
 * Para biriminin ekranda görünen adı. Türkçede sözleşmedeki ad (kapalı
 * listenin kendi adı); öteki dillerde tarayıcının yerleşik çevirisi
 * (`Intl.DisplayNames`), o yoksa yine sözleşmedeki ad, o da yoksa kod.
 */
export function paraBirimiAdi(code: string): string {
  const sozlesmeAdi = CURRENCIES.find((c) => c.code === code)?.name ?? code;
  if (DIL === "tr") return sozlesmeAdi;
  try {
    return new Intl.DisplayNames([YEREL], { type: "currency" }).of(code) ?? sozlesmeAdi;
  } catch {
    return sozlesmeAdi;
  }
}
